import { useQuery } from '@apollo/client';
import { IconClose, IconLeftBottomFoldedPaper, IconZoomIn } from 'assets/icons';
import Loading from 'components/Loading/Loading';
import ModalBase from 'components/Modal/ModalBase';
import SearchInput from 'components/SearchInput/SearchInput';
import { useMutationTicketsSale } from 'containers/SaleDetail/graphQL/hooks/useMutationTicketsSale';
import { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { BookingConnection, BookingEdge, UnfixedTicket } from 'types/api';
import EmptyList from '../components/EmptyList';
import ListLoadingSpinner from '../components/ListLoadingSpinner';
import { GET_BOOKING_LIST } from '../graphQL';
import ConfirmDateInCalendarModal from './ConfirmDateInCalendarModal';
import MultipleUserCard from './MultipleUserCard';
import UserCard from './UserCard';
import { errorHandler } from 'utils/ErrorHandler';

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  position: sticky;
  top: 0;
  left: 0;
  z-index: 10;

  padding: 32px 20px;

  background-color: #fff;

  > h6 {
    margin: 0px;

    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #1b1c1e;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  width: 100%;

  padding: 0 20px;
  margin-bottom: 24px;

  button {
    min-width: 25px;
    height: 44px;
    padding: 0;

    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: rgba(194, 196, 200, 1);

    background-color: transparent;
    border: none;

    cursor: pointer;
  }
`;

const InputBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  width: 100%;
  height: 44px;
  padding: 12px;

  border: 1px solid;
  border-color: rgba(219, 220, 223, 1);
  border-radius: 8px;

  input {
    width: 100%;
    border: none;
    outline: none;

    font-weight: 400;
    font-size: 14px;

    &::placeholder {
      font-weight: 400;
      font-size: 14px;

      color: rgba(152, 155, 162, 1);
    }
  }
`;

const UserListContainer = styled.div`
  display: flex;
  flex-direction: column;

  gap: 16px;

  width: 100%;
  padding: 0 20px;

  .searched-text {
    font-weight: 500;
    font-size: 15px;
    line-height: 22px;

    color: #46474c;

    &.colored {
      color: #0071e9;
    }
  }

  div[data-type='loading-box'] {
    display: flex;
    justify-content: center;
    align-items: center;

    width: 100%;
    height: 300px;
  }
`;

const UserListBox = styled.div`
  display: flex;
  flex-direction: column;

  gap: 12px;

  padding-bottom: 38px;

  overflow-y: scroll;
  max-height: calc(748px - 200px);
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  min-height: auto;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  height: 100%;

  margin-top: 240px;

  > p {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: #46474c;
  }
`;

interface BookingItemsFilter {
  hostId: string;
  unscheduled: boolean;
  customerSearchKeyword: string | null;
  ticketStatusIn: ['APPLIED'];
}

interface AddingScheduleModalProps {
  isMobile: boolean;
  handleModal: () => void;
  totalUnFixedTicketCount: number;
  hostId: string;
  initRefetchStatus: () => void;
  bookingTicketsRefetch: () => void;
  bookingId: string | null;
}

const AddingScheduleModal = ({
  isMobile,
  handleModal,
  totalUnFixedTicketCount,
  hostId,
  initRefetchStatus,
  bookingTicketsRefetch,
  bookingId,
}: AddingScheduleModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string>(''); // 큰 단위. 유저 카드선택시
  const [selectedTicketIdList, setSelectedTicketIdList] = useState<string[]>(
    [],
  ); // 작은 단위. 유저 카드 내부 티켓 선택시

  const [
    isConfirmDateInCalendarModalOpen,
    setIsConfirmDateInCalendarModalOpen,
  ] = useState<boolean>(false);

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [accumulatedEdges, setAccumulatedEdges] = useState<BookingEdge[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const userListBoxRef = useRef<HTMLDivElement>(null);

  const { handleConfirmTickets, handleRestoreTickets } =
    useMutationTicketsSale();

  const [bookingItemsFilter, setBookingItemsFilter] =
    useState<BookingItemsFilter>({
      hostId: hostId,
      ticketStatusIn: ['APPLIED'],
      unscheduled: true,
      customerSearchKeyword: '',
    });

  const bookingIdFilter = {
    hostId: hostId,
    ticketStatusIn: ['APPLIED'],
    idIn: bookingId,
  };

  const {
    data: unFixedBookingItems,
    loading,
    refetch,
  } = useQuery<{
    booking: { bookings: BookingConnection };
  }>(GET_BOOKING_LIST, {
    variables: {
      page: 1,
      size: 100,
      filter: bookingId ? bookingIdFilter : bookingItemsFilter,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (
      unFixedBookingItems?.booking.bookings.edges &&
      isInitialLoad &&
      !loading
    ) {
      setAccumulatedEdges(unFixedBookingItems.booking.bookings.edges);
      setIsInitialLoad(false);
    }
  }, [unFixedBookingItems?.booking.bookings.edges, isInitialLoad, loading]);

  const unFixedTicketList = accumulatedEdges.map(data => data.node);
  const hasNextPage =
    unFixedBookingItems?.booking.bookings?.pageInfo?.hasNextPage || false;

  const handleSearchKeyword = () => {
    setBookingItemsFilter(prev => ({
      ...prev,
      customerSearchKeyword: searchKeyword,
    }));

    setCurrentPage(1);
    setAccumulatedEdges([]);
    setIsInitialLoad(true);
    refetch({
      page: 1,
      size: 100,
      filter: { ...bookingItemsFilter, customerSearchKeyword: searchKeyword },
    });
  };

  const handleLoadMore = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || loading) {
      return;
    }

    const nextPage = currentPage + 1;
    setIsLoadingMore(true);

    try {
      const response = await refetch({
        page: nextPage,
        size: 100,
        filter: bookingId ? bookingIdFilter : bookingItemsFilter,
      });

      if (response?.data?.booking?.bookings?.edges) {
        setAccumulatedEdges(prev => [
          ...prev,
          ...response.data.booking.bookings.edges,
        ]);
        setCurrentPage(nextPage);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasNextPage,
    isLoadingMore,
    loading,
    currentPage,
    bookingId,
    bookingIdFilter,
    bookingItemsFilter,
    refetch,
  ]);

  useEffect(() => {
    const scrollElement = userListBoxRef.current;
    if (!scrollElement) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom < 100;

      if (isNearBottom && !isLoadingMore && !loading && hasNextPage) {
        handleLoadMore();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [isLoadingMore, loading, hasNextPage, handleLoadMore]);

  const handleSelectBookingId = (itemId: string) => {
    setSelectedBookingId(pre => (pre === itemId ? '' : itemId));
  };

  const handleSelectTicketId = (ticketId: string | string[]) => {
    if (typeof ticketId === 'string') {
      /* 개별 선택 */
      setSelectedTicketIdList(pre => {
        if (pre.includes(ticketId)) {
          return pre.filter(id => id !== ticketId);
        }

        return [...pre, ticketId];
      });
    } else {
      /* 전체 선택 */
      setSelectedTicketIdList(pre => {
        if (pre.length === ticketId.length) {
          return [];
        }

        return [...ticketId];
      });
    }
  };

  const keywordHandler = (value: string) => {
    setSearchKeyword(value);
  };

  const handleConfirmDateInCalendarModal = () => {
    setIsConfirmDateInCalendarModalOpen(pre => !pre);
  };

  const submitBookingTime = async (timeStamp: number) => {
    setIsLoading(true);
    await handleConfirmTickets(
      selectedTicketIdList,
      { startedAt: timeStamp, duration: 0 },
      async () => {
        handleConfirmDateInCalendarModal();
        initRefetchStatus();
        setCurrentPage(1);

        // 확정된 티켓을 리스트에서 즉시 제거 (optimistic update)
        const confirmedIds = [...selectedTicketIdList];
        setAccumulatedEdges(prev =>
          prev.filter(edge => {
            const hasRemainingApplied = edge.node.items.some(item =>
              item.tickets.some(
                ticket =>
                  ticket.status === 'APPLIED' &&
                  !confirmedIds.includes(ticket.id),
              ),
            );
            return hasRemainingApplied;
          }),
        );

        setSelectedTicketIdList([]);
        bookingTicketsRefetch();

        try {
          const response = await refetch({
            page: 1,
            size: 100,
            filter: bookingId ? bookingIdFilter : bookingItemsFilter,
          });

          if (response?.data?.booking?.bookings?.edges) {
            setAccumulatedEdges(response.data.booking.bookings.edges);
          } else {
            setAccumulatedEdges([]);
          }
        } catch (error) {
          errorHandler(error);
        }

        setIsLoading(false);
      },
    );
  };

  useEffect(() => {
    setSelectedTicketIdList([]);
  }, [selectedBookingId]);

  useEffect(() => {
    setCurrentPage(1);
    setAccumulatedEdges([]);
    setIsInitialLoad(true);
  }, [bookingItemsFilter.customerSearchKeyword, bookingId]);

  return (
    <>
      {isLoading && <Loading />}
      <ModalBase
        isMobile={isMobile}
        handleModal={handleModal}
        useCustomHeader
        style={{ minHeight: '748px', maxHeight: '748px' }}
      >
        <ModalHeader>
          <h6>일정조율형 예약확정하기</h6>
          <IconClose color={'#70737C'} onClick={handleModal} />
        </ModalHeader>

        <ModalBody>
          {totalUnFixedTicketCount === 0 ? (
            <EmptyContainer>
              <IconLeftBottomFoldedPaper width={40} height={40} />
              <p>조율형 예약 대기 내역이 없어요.</p>
            </EmptyContainer>
          ) : (
            <>
              <InputContainer>
                <SearchInput
                  searchKeyword={searchKeyword}
                  keywordHandler={keywordHandler}
                  handleSearch={handleSearchKeyword}
                  placeholder="크루명이나 번호를 입력해 주세요."
                  isSearched={
                    searchKeyword !== '' &&
                    searchKeyword === bookingItemsFilter.customerSearchKeyword
                  }
                />
              </InputContainer>

              <UserListContainer>
                <p
                  className={`searched-text ${
                    bookingItemsFilter?.customerSearchKeyword ? 'colored' : ''
                  }`}
                >
                  {bookingItemsFilter?.customerSearchKeyword
                    ? `검색적용: ${
                        unFixedTicketList?.length?.toLocaleString() || 0
                      }건 `
                    : bookingId
                    ? `총 예약대기: ${
                        unFixedBookingItems?.booking.bookings?.totalCount?.toLocaleString() ||
                        0
                      }건`
                    : `총 예약대기: ${
                        totalUnFixedTicketCount?.toLocaleString() || 0
                      }건`}
                </p>

                {loading ? (
                  <ListLoadingSpinner />
                ) : unFixedTicketList?.length > 0 ? (
                  <UserListBox ref={userListBoxRef}>
                    {unFixedTicketList?.map((unFixedTicket, idx) => {
                      const isMultipleTicket =
                        unFixedTicket.items[0].tickets.length > 1;

                      return isMultipleTicket ? (
                        <MultipleUserCard
                          TicketData={unFixedTicket}
                          handleSelectBookingId={handleSelectBookingId}
                          selectedBookingId={selectedBookingId}
                          selectedTicketIdList={selectedTicketIdList}
                          handleSelectTicketId={handleSelectTicketId}
                          handleConfirmDateInCalendarModal={
                            handleConfirmDateInCalendarModal
                          }
                          bookingTicketsRefetch={bookingTicketsRefetch}
                          key={`${unFixedTicket.id}-${unFixedTicket.items[0].id}-${idx}`}
                        />
                      ) : (
                        <UserCard
                          TicketData={unFixedTicket}
                          handleSelectBookingId={handleSelectBookingId}
                          selectedBookingId={selectedBookingId}
                          selectedTicketIdList={selectedTicketIdList}
                          handleSelectTicketId={handleSelectTicketId}
                          handleConfirmDateInCalendarModal={
                            handleConfirmDateInCalendarModal
                          }
                          bookingTicketsRefetch={bookingTicketsRefetch}
                          key={`${unFixedTicket.id}-${unFixedTicket.items[0].id}-${idx}`}
                        />
                      );
                    })}
                    {isLoadingMore && (
                      <LoadingSpinnerContainer>
                        <ListLoadingSpinner
                          minHeight="auto"
                          showText={false}
                          marginBottom="0"
                          padding="20px 16px 12px"
                        />
                      </LoadingSpinnerContainer>
                    )}
                  </UserListBox>
                ) : (
                  <EmptyList text="조율형 예약 대기 내역이 없어요." />
                )}
              </UserListContainer>
            </>
          )}
        </ModalBody>

        {/* 시간 정하는 컐린더 모달 */}
        {isConfirmDateInCalendarModalOpen && (
          <ConfirmDateInCalendarModal
            handleModal={handleConfirmDateInCalendarModal}
            submitBookingTime={submitBookingTime}
          />
        )}
      </ModalBase>
    </>
  );
};

export default AddingScheduleModal;
